import { type ReactNode, useEffect, useState } from 'react';
import { Icon } from './icons';

export function PageHeader({
  title,
  subtitle,
  actions
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
      <div>
        <h2 className="text-2xl font-display font-bold tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-stone-400 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  icon,
  tone = 'amber'
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: 'amber' | 'green' | 'sky' | 'red' | 'stone';
}) {
  const ringMap = {
    amber: 'ring-amber-400/20 text-amber-300 bg-amber-400/10',
    green: 'ring-emerald-400/20 text-emerald-300 bg-emerald-400/10',
    sky: 'ring-sky-400/20 text-sky-300 bg-sky-400/10',
    red: 'ring-red-400/20 text-red-300 bg-red-400/10',
    stone: 'ring-white/10 text-stone-300 bg-white/5'
  } as const;
  return (
    <div className="stat-card">
      <div className="flex items-center justify-between">
        <span className="label">{label}</span>
        {icon && <span className={`w-7 h-7 rounded-lg ring-1 flex items-center justify-center ${ringMap[tone]}`}>{icon}</span>}
      </div>
      <div className="value">{value}</div>
      {hint && <div className="delta">{hint}</div>}
    </div>
  );
}

export function SectionCard({
  title,
  actions,
  children,
  className = ''
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`card p-4 md:p-5 ${className}`}>
      {title && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold tracking-wide text-stone-200">{title}</h3>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function EmptyState({ title, hint, icon }: { title: string; hint?: string; icon?: ReactNode }) {
  return (
    <div className="py-10 flex flex-col items-center justify-center text-center">
      <div className="w-10 h-10 rounded-xl bg-white/5 ring-1 ring-white/10 flex items-center justify-center text-stone-400 mb-3">
        {icon ?? <Icon.Spark size={18} />}
      </div>
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}

export function Badge({ children, tone = 'stone' }: { children: ReactNode; tone?: 'amber' | 'green' | 'red' | 'stone' | 'sky' }) {
  const map = {
    amber: 'pill-amber',
    green: 'pill-green',
    red: 'pill-red',
    stone: 'pill-stone',
    sky: 'pill-sky'
  } as const;
  return <span className={`pill ${map[tone]}`}>{children}</span>;
}

export function Toggle({
  pressed,
  onChange,
  disabled,
  ariaLabel
}: {
  pressed: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={pressed}
      aria-pressed={pressed}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!pressed)}
      className="toggle"
    >
      <span className="toggle__thumb" />
    </button>
  );
}

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" opacity="0.2" />
      <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
    </svg>
  );
}

export function useDebounce<T>(value: T, ms = 250): T {
  const [v, setV] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
